import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, Table } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useUIDispatchContext } from 'providers/UI';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useNavigate } from 'react-router-dom';
import { deleteBlog, fetchBlogList } from 'services/api/blog';
import { BlogListRow } from 'types/api/blog';

import useOrgEffect from 'hooks/useOrgEffect';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { OrganizationAlert, Private } from 'components/shared';

const PAGE_LIMIT = 10;

// Admin list view is pinned to English — same as the legacy GQL
// `lang: 'en'` hard-code in the previous implementation.
const LIST_LANG = 'en';

const BlogNews: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const [rows, setRows] = useState<BlogListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const { toggleModal } = useUIDispatchContext();

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchBlogList({ page, limit: PAGE_LIMIT }, LIST_LANG);

        if (cancelled) return;
        setRows(res.data?.data ?? []);
        setTotal(res.data?.total ?? 0);
      } catch {
        // swallow — empty table on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const onDeleteBlog = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        onConfirm: async () => {
          try {
            await deleteBlog(id);
            setRefreshKey(k => k + 1);
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [toggleModal]
  );

  const columns: ColumnProps<BlogListRow>[] = useMemo(
    () => [
      {
        title: t('id'),
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('blog.title'),
        dataIndex: 'title',
        key: 'title'
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: id => (
          <ActionMenu
            data={[
              {
                title: t('table.edit'),
                permission: {
                  route: PERMISSION_ROUTES.blog,
                  action: PERMISSION_ACTIONS.update
                },
                action: () => navigate(`/blog-news/details/${id}`)
              },
              {
                title: t('table.delete'),
                permission: {
                  route: PERMISSION_ROUTES.blog,
                  action: PERMISSION_ACTIONS.update
                },
                action: () => onDeleteBlog(id),
                menuProps: {
                  style: {
                    color: 'var(--danger-color)'
                  }
                }
              }
            ]}
            placement="bottomRight"
            icon={<EllipsisOutlined />}
          />
        )
      }
    ],
    [navigate, onDeleteBlog, t]
  );

  return (
    <PageWrapper
      title={t('blog.page_title')}
      color="white"
      extra={[
        <Private
          key="add-blog-button"
          route={PERMISSION_ROUTES.blog}
          action={PERMISSION_ACTIONS.create}
        >
          <OrganizationAlert>
            <Button
              type="primary"
              onClick={() => navigate('/blog-news/details')}
            >
              <PlusOutlined />
              {t('blog.add_new')}
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Table
        dataSource={rows}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default BlogNews;
